import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { Bot, Sparkles, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface AgentShapeNodeData {
  label: string
  agentName?: string
  agentId?: string
  skillName?: string
  status?: 'idle' | 'configured' | 'running' | 'success' | 'error'
  outputVariable?: string
  errorMessage?: string
  onDelete?: () => void
  [key: string]: unknown
}

const statusConfig = {
  idle: { color: '#8b5cf6', icon: Bot, animate: false },
  configured: { color: '#8b5cf6', icon: Sparkles, animate: false },
  running: { color: '#8b5cf6', icon: Loader2, animate: true },
  success: { color: '#10b981', icon: CheckCircle2, animate: false },
  error: { color: '#ef4444', icon: AlertCircle, animate: false },
}

interface AgentShapeNodeProps {
  data: AgentShapeNodeData
  selected?: boolean
}

function AgentShapeNodeComponent({ data, selected }: AgentShapeNodeProps) {
  const { label, agentName, skillName, status = 'idle', outputVariable, errorMessage } = data
  const config = statusConfig[status]
  const StatusIcon = config.icon
  const displayName = agentName || label || 'AI Agent'

  return (
    <div
      className="relative group"
      style={{ width: 200, height: 90 }}
    >
      {/* Hexagon shape - distinctive for AI/processing nodes */}
      <svg
        viewBox="0 0 200 90"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: selected
            ? `drop-shadow(0 0 16px ${config.color}40)`
            : 'drop-shadow(0 4px 8px rgba(0,0,0,0.08))',
        }}
      >
        <defs>
          <linearGradient id={`agent-grad-${status}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.color} stopOpacity="0.18" />
            <stop offset="50%" stopColor={config.color} stopOpacity="0.08" />
            <stop offset="100%" stopColor={config.color} stopOpacity="0.15" />
          </linearGradient>
          <filter id="agent-glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Hexagon path */}
        <path
          d="M 40 4 L 160 4 L 196 45 L 160 86 L 40 86 L 4 45 Z"
          fill={`url(#agent-grad-${status})`}
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        {/* Inner hexagon accent */}
        <path
          d="M 48 12 L 152 12 L 184 45 L 152 78 L 48 78 L 16 45 Z"
          fill="none"
          stroke={config.color}
          strokeWidth="1"
          strokeOpacity="0.15"
        />
        {/* Decorative circuit lines */}
        <line x1="16" y1="45" x2="4" y2="45" stroke={config.color} strokeWidth="1" strokeOpacity="0.3" />
        <line x1="184" y1="45" x2="196" y2="45" stroke={config.color} strokeWidth="1" strokeOpacity="0.3" />
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex items-center px-8">
        {/* Icon with status */}
        <div className="relative flex-shrink-0 mr-3">
          <div
            className="p-2.5 rounded-xl"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <Bot className="w-5 h-5" style={{ color: config.color }} />
          </div>
          {/* Status badge */}
          {status !== 'idle' && (
            <div
              className={cn(
                'absolute -bottom-1 -right-1 p-0.5 rounded-full bg-white dark:bg-gray-800',
                config.animate && 'animate-spin'
              )}
            >
              <StatusIcon className="w-3 h-3" style={{ color: config.color }} />
            </div>
          )}
        </div>

        {/* Text content */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {displayName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {skillName || (status === 'error' ? 'Error' : 'Click to configure')}
          </p>
        </div>
      </div>

      {/* Error tooltip */}
      {status === 'error' && errorMessage && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity z-50">
          <div className="mt-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg shadow-lg max-w-[200px]">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Output variable badge */}
      {outputVariable && (
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
          <span className="px-2 py-0.5 text-[10px] font-mono bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-full border border-purple-200 dark:border-purple-700">
            {`{{${outputVariable}}}`}
          </span>
        </div>
      )}

      {/* Target Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
        style={{ backgroundColor: config.color }}
      />

      {/* Source Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
        style={{ backgroundColor: config.color }}
      />
    </div>
  )
}

export const AgentShapeNode = memo(AgentShapeNodeComponent)
export default AgentShapeNode
